import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { naira } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { code: "", type: "percent", value: "", minSpend: "", usageLimit: "", expiresAt: "", active: true };

export default function CouponsAdmin() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", {
        ...form,
        value: Number(form.value),
        minSpend: Number(form.minSpend || 0),
        usageLimit: form.usageLimit === "" ? null : Number(form.usageLimit),
        expiresAt: form.expiresAt || null,
      });
      toast.success("Coupon created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    load();
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-serif text-3xl text-espresso">Coupons</h1>
          <p className="text-bark text-sm">{coupons.length} codes</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-coupon-button" className="bg-terracotta text-bone hover:bg-espresso">
          <Plus className="w-4 h-4 mr-1" /> New coupon
        </Button>
      </div>

      <div className="border border-sandline rounded-xl overflow-hidden">
        {/* Desktop header */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-sand text-bark text-xs uppercase tracking-wide font-medium">
          <span className="col-span-3">Code</span>
          <span className="col-span-3">Discount</span>
          <span className="col-span-2">Used</span>
          <span className="col-span-2">Active</span>
          <span className="col-span-2 text-right">Actions</span>
        </div>
        {coupons.map((c) => (
          <div key={c.id} className="border-t border-sandline first:border-t-0 md:first:border-t" data-testid={`coupon-row-${c.id}`}>
            {/* Desktop row */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm">
              <span className="col-span-3 font-mono text-espresso font-medium">{c.code}</span>
              <span className="col-span-3 text-bark">{c.type === "percent" ? `${c.value}%` : naira(c.value)}</span>
              <span className="col-span-2 text-bark">{c.timesUsed || 0}{c.usageLimit ? `/${c.usageLimit}` : ""}</span>
              <span className="col-span-2">{c.active ? <span className="text-moss">Yes</span> : <span className="text-bark">No</span>}</span>
              <div className="col-span-2 flex justify-end">
                <button onClick={() => del(c.id)} data-testid={`delete-coupon-${c.id}`} className="p-1.5 text-bark hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {/* Mobile card */}
            <div className="md:hidden p-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-espresso font-medium">{c.code}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-bark">
                  <span>{c.type === "percent" ? `${c.value}% off` : `${naira(c.value)} off`}</span>
                  <span>·</span>
                  <span>Used {c.timesUsed || 0}{c.usageLimit ? `/${c.usageLimit}` : ""}</span>
                  <span>·</span>
                  <span className={c.active ? "text-moss" : "text-bark"}>{c.active ? "Active" : "Inactive"}</span>
                </div>
              </div>
              <button onClick={() => del(c.id)} data-testid={`delete-coupon-mobile-${c.id}`} aria-label="Delete" className="w-10 h-10 rounded-lg border border-sandline flex items-center justify-center text-destructive active:bg-destructive/10 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {coupons.length === 0 && <p className="p-5 text-bark text-sm">No coupons yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-bone w-[calc(100%-1rem)] max-h-[92vh] overflow-y-auto p-5 md:p-6" data-testid="coupon-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-espresso">New coupon</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><label className="text-sm text-bark mb-1 block">Code</label><Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input" className="border-sandline font-mono" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-bark mb-1 block">Type</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="coupon-type-select" className="border-sandline"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat (₦)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-sm text-bark mb-1 block">Value</label><Input type="number" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="coupon-value-input" className="border-sandline" /></div>
              <div><label className="text-sm text-bark mb-1 block">Min spend (₦)</label><Input type="number" value={form.minSpend} onChange={(e) => setForm({ ...form, minSpend: e.target.value })} className="border-sandline" /></div>
              <div><label className="text-sm text-bark mb-1 block">Usage limit</label><Input type="number" value={form.usageLimit} onChange={(e) => setForm({ ...form, usageLimit: e.target.value })} className="border-sandline" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-espresso"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
            <DialogFooter><Button type="submit" data-testid="save-coupon-button" className="bg-terracotta text-bone hover:bg-espresso">Create</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
